import type {
    Bi,
    Gender,
    Lakara,
    Pada,
    Purusha,
    ReviewStatus,
    Vacana,
    Vibhakti
} from '@/sanskrit/lib/sanskrit/types'
import type { NounTable } from '@/sanskrit/lib/sanskrit/types'
import type { KridantaId } from '@/sanskrit/lib/sanskrit/derivation'
import type { StemClass } from '@/sanskrit/lib/sanskrit/nouns'

import { ALL_DHATUS } from '@/sanskrit/lib/sanskrit/dhatus'
import { allVerbCells } from '@/sanskrit/lib/sanskrit/paradigms'
import { KRIDANTA_FORMS } from '@/sanskrit/lib/sanskrit/derivation'
import { VACANAS, VIBHAKTIS } from '@/sanskrit/lib/sanskrit/types'
import { lookupKey, tokenizeSentence } from '@/sanskrit/lib/sanskrit/script'
import { VOCABULARY } from '@/sanskrit/lib/sanskrit/vocabulary'

import {
    ALL_PARADIGMS,
    NUMERALS,
    NUMERAL_TABLES,
    declineStem,
    getNounParadigm
} from '@/sanskrit/lib/sanskrit/nouns'

export type Analysis =
    | {
        kind: 'verb'
        form: string
        dhatuId: string
        lakara: Lakara
        pada: Pada
        purusha: Purusha
        vacana: Vacana
        status: ReviewStatus
        variant: boolean
    }
    | {
        kind: 'noun' | 'pronoun' | 'adjective'
        form: string
        stem: string
        lemma: string
        gender: Gender
        vibhakti: Vibhakti
        vacana: Vacana
        vocabId?: string
        paradigmId?: string
        cls?: StemClass
        purusha: Purusha
        status: ReviewStatus
        variant: boolean
        meaning: Bi
    }
    | {
        kind: 'numeral'
        form: string
        numeralId: string
        value: number
        gender?: Gender
        vibhakti?: Vibhakti
        vacana?: Vacana
        meaning: Bi
    }
    | {
        kind: 'kridanta'
        form: string
        dhatuId: string
        pratyaya: KridantaId
        gender?: Gender
        meaning: Bi
        status: ReviewStatus
    }
    | {
        kind: 'indeclinable' | 'phrase'
        form: string
        vocabId: string
        meaning: Bi
    }
    | {
        kind: 'derived'
        form: string
        dhatuId: string
        meaning: Bi
    }

export type NominalAnalysis = Extract<Analysis, { kind: 'noun' | 'pronoun' | 'adjective' }>
export type VerbAnalysis = Extract<Analysis, { kind: 'verb' }>

let INDEX: Map<string, Analysis[]> | null = null

const add = (index: Map<string, Analysis[]>, form: string, analysis: Analysis) => {
    if (!form) return
    const key = lookupKey(form)
    const list = index.get(key) ?? []
    list.push(analysis)
    index.set(key, list)
}

const addTable = (
    index: Map<string, Analysis[]>,
    table: NounTable,
    base: {
        kind: 'noun' | 'pronoun' | 'adjective'
        stem: string
        lemma: string
        gender: Gender
        vocabId?: string
        paradigmId?: string
        cls?: StemClass
        purusha?: Purusha
        status: ReviewStatus
        meaning: Bi
    }
) => {
    for (const vib of VIBHAKTIS) {
        for (const v of VACANAS) {
            table[vib][v].forEach((form, i) => {
                add(index, form, {
                    ...base,
                    kind: base.kind,
                    form,
                    vibhakti: vib,
                    vacana: v,
                    purusha: base.purusha ?? 'prathama',
                    variant: i > 0
                })
            })
        }
    }
}

/** Extra single forms used in the course that come from stems not yet carried as full tables. */
const EXTRA_NOMINALS: Array<{ form: string; stem: string; lemma: string; gender: Gender; vibhakti: Vibhakti; vacana: Vacana; meaning: Bi; kind: 'noun' | 'adjective' | 'pronoun' }> = [
    { form: 'पूर्वस्याम्', stem: 'पूर्वा', lemma: 'पूर्वा', gender: 'f', vibhakti: 7, vacana: 'eka', meaning: { hi: 'पूर्व (दिशा) में', en: 'in the east' }, kind: 'adjective' },
    { form: 'दिशि', stem: 'दिश्', lemma: 'दिक्', gender: 'f', vibhakti: 7, vacana: 'eka', meaning: { hi: 'दिशा में', en: 'in the direction' }, kind: 'noun' },
    { form: 'दिक्', stem: 'दिश्', lemma: 'दिक्', gender: 'f', vibhakti: 1, vacana: 'eka', meaning: { hi: 'दिशा', en: 'direction' }, kind: 'noun' },
    { form: 'कुशली', stem: 'कुशलिन्', lemma: 'कुशली', gender: 'm', vibhakti: 1, vacana: 'eka', meaning: { hi: 'कुशल, ठीक', en: 'well, fine' }, kind: 'adjective' },
    { form: 'कुशलिनी', stem: 'कुशलिन्', lemma: 'कुशली', gender: 'f', vibhakti: 1, vacana: 'eka', meaning: { hi: 'कुशल, ठीक (स्त्री)', en: 'well, fine (fem.)' }, kind: 'adjective' },
    { form: 'विद्वान्', stem: 'विद्वस्', lemma: 'विद्वान्', gender: 'm', vibhakti: 1, vacana: 'eka', meaning: { hi: 'विद्वान्', en: 'learned, a scholar' }, kind: 'adjective' },
    { form: 'सुखिनः', stem: 'सुखिन्', lemma: 'सुखी', gender: 'm', vibhakti: 1, vacana: 'bahu', meaning: { hi: 'सुखी', en: 'happy' }, kind: 'adjective' },
    { form: 'विजयी', stem: 'विजयिन्', lemma: 'विजयी', gender: 'm', vibhakti: 1, vacana: 'eka', meaning: { hi: 'विजयी', en: 'victorious' }, kind: 'adjective' },
    { form: 'सुवृष्टिः', stem: 'सुवृष्टि', lemma: 'सुवृष्टिः', gender: 'f', vibhakti: 1, vacana: 'eka', meaning: { hi: 'अच्छी वर्षा', en: 'good rain' }, kind: 'noun' },
    { form: 'सुभिक्षम्', stem: 'सुभिक्ष', lemma: 'सुभिक्षम्', gender: 'n', vibhakti: 1, vacana: 'eka', meaning: { hi: 'अन्न की भरपूरता', en: 'plenty of food' }, kind: 'noun' },
    { form: 'लक्ष्मणः', stem: 'लक्ष्मण', lemma: 'लक्ष्मणः', gender: 'm', vibhakti: 1, vacana: 'eka', meaning: { hi: 'लक्ष्मण', en: 'Lakṣmaṇa' }, kind: 'noun' }
]

const EXTRA_VERBS: Array<{ form: string; dhatuId: string; lakara: Lakara; purusha: Purusha; vacana: Vacana; pada: Pada }> = []

const PHRASE_FORMS: Array<{ form: string; meaning: Bi }> = [
    { form: 'स्वीकरोतु', meaning: { hi: 'स्वीकार कीजिए (स्वी + कृ, लोट्)', en: 'please accept / take it (स्वी + कृ, imperative)' } },
    { form: 'स्वीकरोति', meaning: { hi: 'स्वीकार करता है (स्वी + कृ, लट्)', en: 'accepts (स्वी + कृ, present)' } },
    { form: 'शोभनम्', meaning: { hi: 'बहुत अच्छा!', en: 'excellent!' } },
    { form: 'अस्तु', meaning: { hi: 'ठीक है, ऐसा ही हो', en: 'all right, so be it' } }
]

const buildIndex = (): Map<string, Analysis[]> => {
    const index = new Map<string, Analysis[]>()

    for (const d of ALL_DHATUS) {
        for (const cell of allVerbCells(d.id)) {
            cell.forms.forEach((form, i) => {
                add(index, form, {
                    kind: 'verb',
                    form,
                    dhatuId: d.id,
                    lakara: cell.lakara,
                    pada: cell.pada,
                    purusha: cell.p,
                    vacana: cell.v,
                    status: cell.status,
                    variant: i > 0
                })
            })
        }

        for (const r of d.related) {
            add(index, r.sa, { kind: 'derived', form: r.sa, dhatuId: d.id, meaning: r.meaning })
        }
    }

    for (const e of EXTRA_VERBS) {
        add(index, e.form, { kind: 'verb', form: e.form, dhatuId: e.dhatuId, lakara: e.lakara, pada: e.pada, purusha: e.purusha, vacana: e.vacana, status: 'verified', variant: false })
    }

    const covered = new Set<string>()

    for (const entry of VOCABULARY) {
        if (entry.pos === 'noun') {
            const paradigm = entry.paradigmId ? getNounParadigm(entry.paradigmId) : undefined
            const table = paradigm?.table ?? (entry.cls && entry.stem ? declineStem(entry.stem, entry.cls) : null)
            if (!table) continue

            covered.add(`${entry.stem}|${entry.gender}`)
            addTable(index, table, {
                kind: 'noun',
                stem: entry.stem ?? entry.sa,
                lemma: entry.sa,
                gender: entry.gender ?? 'm',
                vocabId: entry.id,
                paradigmId: entry.paradigmId,
                cls: entry.cls,
                status: entry.status,
                meaning: entry.meaning
            })
        }
        else if (entry.pos === 'adjective' && entry.stem && !['purva', 'pascima', 'uttara-dik', 'daksina'].includes(entry.id)) {
            const genders = entry.adjGenders ?? (['m', 'f', 'n'] as Gender[])

            for (const g of genders) {
                let table: NounTable
                let stem = entry.stem

                if (g === 'm') table = declineStem(entry.stem, entry.adjMascCls ?? 'a-m')
                else if (g === 'n') table = declineStem(entry.stem, 'a-n')
                else {
                    stem = entry.fem?.stem ?? entry.stem + 'ा'
                    table = declineStem(stem, entry.fem?.cls ?? 'aa-f')
                }

                addTable(index, table, {
                    kind: 'adjective',
                    stem,
                    lemma: entry.sa,
                    gender: g,
                    vocabId: entry.id,
                    status: entry.status,
                    meaning: entry.meaning
                })
            }

            if (entry.id === 'harita') {
                addTable(index, declineStem('हरिणी', 'ii-f'), { kind: 'adjective', stem: 'हरिणी', lemma: entry.sa, gender: 'f', vocabId: entry.id, status: entry.status, meaning: entry.meaning })
            }
        }
        else if (entry.pos === 'indeclinable' || entry.pos === 'phrase' || entry.pos === 'adjective') {
            add(index, entry.sa, { kind: entry.pos === 'phrase' ? 'phrase' : 'indeclinable', form: entry.sa, vocabId: entry.id, meaning: entry.meaning })
        }
        else if (entry.pos === 'numeral') {
            add(index, entry.sa, { kind: 'indeclinable', form: entry.sa, vocabId: entry.id, meaning: entry.meaning })
        }
    }

    for (const paradigm of ALL_PARADIGMS) {
        if (paradigm.kind === 'noun' && covered.has(`${paradigm.stem}|${paradigm.gender}`)) continue

        addTable(index, paradigm.table, {
            kind: paradigm.kind === 'pronoun' ? 'pronoun' : 'noun',
            stem: paradigm.stem,
            lemma: paradigm.lemma,
            gender: paradigm.gender,
            paradigmId: paradigm.id,
            cls: paradigm.cls,
            purusha: paradigm.purusha ?? 'prathama',
            status: paradigm.status,
            meaning: paradigm.meaning
        })
    }

    for (const n of NUMERAL_TABLES) {
        const info = NUMERALS.find(x => x.id === n.id)

        for (const vib of VIBHAKTIS) {
            for (const v of VACANAS) {
                for (const form of n.table[vib][v]) {
                    add(index, form, {
                        kind: 'numeral',
                        form,
                        numeralId: n.id,
                        value: n.value,
                        gender: n.gender,
                        vibhakti: vib,
                        vacana: v,
                        meaning: { hi: `संख्या ${n.value}`, en: `number ${n.value}` }
                    })
                }
            }
        }

        if (info && !index.has(lookupKey(info.counting))) {
            add(index, info.counting, { kind: 'numeral', form: info.counting, numeralId: info.id, value: info.value, meaning: { hi: `संख्या ${info.value}`, en: `number ${info.value}` } })
        }
    }

    for (const info of NUMERALS.filter(x => x.value >= 5)) {
        add(index, info.counting, { kind: 'numeral', form: info.counting, numeralId: info.id, value: info.value, meaning: { hi: `संख्या ${info.value}`, en: `number ${info.value}` } })
    }

    for (const k of KRIDANTA_FORMS) {
        for (const form of [k.form, ...(k.variants ?? [])]) {
            add(index, form, { kind: 'kridanta', form, dhatuId: k.dhatuId, pratyaya: k.pratyaya, gender: k.gender, meaning: k.meaning, status: k.status })
        }
    }

    for (const e of EXTRA_NOMINALS) {
        add(index, e.form, { ...e, purusha: 'prathama', status: 'verified', variant: false })
    }

    for (const p of PHRASE_FORMS) {
        add(index, p.form, { kind: 'phrase', form: p.form, vocabId: '', meaning: p.meaning })
    }

    return index
}

export const getIndex = (): Map<string, Analysis[]> => {
    if (!INDEX) INDEX = buildIndex()
    return INDEX
}

/** All analyses for one word (order: verified, non-variant first). */
export const analyzeWord = (word: string): Analysis[] => {
    const list = getIndex().get(lookupKey(word)) ?? []

    return [...list].sort((a, b) => {
        const va = 'variant' in a && a.variant ? 1 : 0
        const vb = 'variant' in b && b.variant ? 1 : 0
        return va - vb
    })
}

export const isKnownWord = (word: string): boolean => analyzeWord(word).length > 0

/**
 * Selector syntax for disambiguating a token in curated text:
 *   v:<dhatuId>:<lakara>:<purusha>:<vacana>   n:<stem>:<vibhakti>:<vacana>   p:<paradigmId>:<vibhakti>:<vacana>
 */
export const matchesSelector = (a: Analysis, selector: string): boolean => {
    const [kind, ...rest] = selector.split(':')

    if (kind === 'v' && a.kind === 'verb') {
        const [dhatuId, lakara, purusha, vacana] = rest
        return a.dhatuId === dhatuId && (!lakara || a.lakara === lakara) && (!purusha || a.purusha === purusha) && (!vacana || a.vacana === vacana)
    }

    if ((kind === 'n' || kind === 'a') && (a.kind === 'noun' || a.kind === 'adjective')) {
        const [stem, vib, vacana] = rest
        return (a.stem === stem || a.lemma === stem) && (!vib || String(a.vibhakti) === vib) && (!vacana || a.vacana === vacana)
    }

    if (kind === 'p' && a.kind === 'pronoun') {
        const [paradigmId, vib, vacana] = rest
        return a.paradigmId === paradigmId && (!vib || String(a.vibhakti) === vib) && (!vacana || a.vacana === vacana)
    }

    if (kind === 'k' && a.kind === 'kridanta') return true
    if (kind === 'i' && (a.kind === 'indeclinable' || a.kind === 'phrase' || a.kind === 'numeral')) return true

    return false
}

export const pickAnalysis = (word: string, selector?: string): { primary: Analysis | null; others: Analysis[] } => {
    const all = analyzeWord(word)
    if (all.length === 0) return { primary: null, others: [] }

    const hit = selector ? all.find(a => matchesSelector(a, selector)) : undefined
    const primary = hit ?? all[0]

    return { primary, others: all.filter(a => a !== primary) }
}

/* ------------------------------------------------------------------ */
/* Agreement and sentence validation                                    */
/* ------------------------------------------------------------------ */

const PERSON_RANK: Record<Purusha, number> = { prathama: 0, madhyama: 1, uttama: 2 }

export interface SentenceIssue {
    code: 'missing' | 'extra' | 'person' | 'number' | 'case' | 'form' | 'gender' | 'no-verb' | 'order'
    text: Bi
}

export interface SentenceResult {
    correct: boolean
    issues: SentenceIssue[]
    note?: Bi
}

const nominalOf = (word: string): NominalAnalysis[] => analyzeWord(word).filter((a): a is NominalAnalysis => a.kind === 'noun' || a.kind === 'pronoun' || a.kind === 'adjective')

const verbsOf = (word: string): VerbAnalysis[] => analyzeWord(word).filter((a): a is VerbAnalysis => a.kind === 'verb')

const PURUSHA_HI: Record<Purusha, string> = { prathama: 'प्रथम पुरुष', madhyama: 'मध्यम पुरुष', uttama: 'उत्तम पुरुष' }
const PURUSHA_EN: Record<Purusha, string> = { prathama: 'प्रथम puruṣa (he/she/it/they)', madhyama: 'मध्यम puruṣa (you)', uttama: 'उत्तम puruṣa (I/we)' }
const VACANA_HI: Record<Vacana, string> = { eka: 'एकवचन', dvi: 'द्विवचन', bahu: 'बहुवचन' }
const VACANA_EN: Record<Vacana, string> = { eka: 'singular', dvi: 'dual', bahu: 'plural' }
const VIB_HI: Record<Vibhakti, string> = { 1: 'प्रथमा', 2: 'द्वितीया', 3: 'तृतीया', 4: 'चतुर्थी', 5: 'पञ्चमी', 6: 'षष्ठी', 7: 'सप्तमी', 8: 'सम्बोधन' }
const VIB_EN: Record<Vibhakti, string> = { 1: 'प्रथमा (subject case)', 2: 'द्वितीया (object case)', 3: 'तृतीया (instrumental)', 4: 'चतुर्थी (dative)', 5: 'पञ्चमी (ablative)', 6: 'षष्ठी (genitive)', 7: 'सप्तमी (locative)', 8: 'सम्बोधन (vocative)' }

/** Combine several subjects joined by च: person takes the highest, number grows. */
export const combineSubjects = (subjects: Array<{ purusha: Purusha; vacana: Vacana }>): { purusha: Purusha; vacana: Vacana } => {
    if (subjects.length === 1) return subjects[0]

    const purusha = subjects.reduce<Purusha>((best, s) => (PERSON_RANK[s.purusha] > PERSON_RANK[best] ? s.purusha : best), 'prathama')
    const count = subjects.reduce((n, s) => n + (s.vacana === 'eka' ? 1 : s.vacana === 'dvi' ? 2 : 3), 0)

    return { purusha, vacana: count === 2 ? 'dvi' : 'bahu' }
}

/** Check subject-verb and noun-adjective agreement on a bag of words. */
export const checkAgreement = (words: string[]): SentenceIssue[] => {
    const issues: SentenceIssue[] = []
    const verbWords = words.filter(w => verbsOf(w).length > 0)

    if (verbWords.length === 0) return issues

    const verbWord = verbWords[verbWords.length - 1]
    const verbOptions = verbsOf(verbWord)
    const hasCa = words.some(w => lookupKey(w) === 'च')

    const subjectWords = words.filter(w => w !== verbWord && nominalOf(w).some(a => a.vibhakti === 1 && a.kind !== 'adjective'))
    const unambiguous = subjectWords.filter(w => nominalOf(w).filter(a => a.kind !== 'adjective').every(a => a.vibhakti === 1))
    const candidates = unambiguous.length > 0 ? unambiguous : subjectWords.slice(0, 1)

    if (candidates.length === 0) return issues

    const subjectAnalyses = candidates.map(w => {
        const nom = nominalOf(w).filter(a => a.vibhakti === 1 && a.kind !== 'adjective')
        const choice = nom.find(a => verbOptions.some(v => v.purusha === a.purusha && v.vacana === a.vacana)) ?? nom[0]
        return { word: w, purusha: choice.purusha, vacana: choice.vacana }
    })

    const used = hasCa && subjectAnalyses.length > 1 ? subjectAnalyses : [subjectAnalyses[0]]
    const need = combineSubjects(used)
    const ok = verbOptions.some(v => v.purusha === need.purusha && v.vacana === need.vacana)

    if (!ok) {
        const v = verbOptions[0]
        const subjectText = used.map(s => s.word).join(' + ')

        if (v.purusha !== need.purusha) {
            issues.push({
                code: 'person',
                text: {
                    hi: `${verbWord} ${PURUSHA_HI[v.purusha]} का रूप है, पर कर्ता ${subjectText} ${PURUSHA_HI[need.purusha]} है। क्रिया का पुरुष कर्ता से मिलना चाहिए।`,
                    en: `${verbWord} is ${PURUSHA_EN[v.purusha]}, but the subject ${subjectText} is ${PURUSHA_EN[need.purusha]}. The verb must match the subject’s person.`
                }
            })
        }
        else {
            issues.push({
                code: 'number',
                text: {
                    hi: `${verbWord} ${VACANA_HI[v.vacana]} है, पर कर्ता ${subjectText} ${VACANA_HI[need.vacana]} चाहता है।`,
                    en: `${verbWord} is ${VACANA_EN[v.vacana]}, but the subject ${subjectText} needs the ${VACANA_EN[need.vacana]}.`
                }
            })
        }
    }

    return issues
}

const describeDiff = (chosen: string, expected: string): SentenceIssue | null => {
    const cv = verbsOf(chosen)
    const ev = verbsOf(expected)

    if (cv.length > 0 && ev.length > 0 && cv[0].dhatuId === ev[0].dhatuId) {
        const c = cv[0]
        const e = ev[0]

        if (c.lakara !== e.lakara) {
            return {
                code: 'form',
                text: {
                    hi: `${chosen} दूसरे लकार का रूप है; यहाँ ${expected} चाहिए।`,
                    en: `${chosen} is in a different lakāra; here you need ${expected}.`
                }
            }
        }

        return {
            code: c.purusha !== e.purusha ? 'person' : 'number',
            text: {
                hi: `${chosen} = ${PURUSHA_HI[c.purusha]}, ${VACANA_HI[c.vacana]}। यहाँ ${PURUSHA_HI[e.purusha]}, ${VACANA_HI[e.vacana]} चाहिए: ${expected}।`,
                en: `${chosen} is ${PURUSHA_EN[c.purusha]}, ${VACANA_EN[c.vacana]}. Here you need ${PURUSHA_EN[e.purusha]}, ${VACANA_EN[e.vacana]}: ${expected}.`
            }
        }
    }

    const cn = nominalOf(chosen)
    const en = nominalOf(expected)

    if (cn.length > 0 && en.length > 0 && cn[0].stem === en[0].stem) {
        const c = cn[0]
        const e = en.find(a => a.vibhakti !== c.vibhakti || a.vacana !== c.vacana) ?? en[0]

        if (c.vacana !== e.vacana && c.vibhakti === e.vibhakti) {
            return {
                code: 'number',
                text: {
                    hi: `${chosen} ${VACANA_HI[c.vacana]} है; यहाँ ${VACANA_HI[e.vacana]} चाहिए: ${expected}।`,
                    en: `${chosen} is ${VACANA_EN[c.vacana]}; here you need the ${VACANA_EN[e.vacana]}: ${expected}.`
                }
            }
        }

        return {
            code: 'case',
            text: {
                hi: `${chosen} ${VIB_HI[c.vibhakti]} विभक्ति में है; यहाँ ${VIB_HI[e.vibhakti]} चाहिए: ${expected}।`,
                en: `${chosen} is in ${VIB_EN[c.vibhakti]}; here it must be ${VIB_EN[e.vibhakti]}: ${expected}.`
            }
        }
    }

    return null
}

const bag = (words: string[]): string[] => words.map(lookupKey).sort()

/**
 * Validate a built sentence against the curated answer.
 * Compares grammar, not strings: any word order is accepted when `orderFree` (the default),
 * and every wrong choice is explained from its grammatical features.
 */
export const validateSentence = (chosen: string[], expected: string[], orderFree = true): SentenceResult => {
    const c = bag(chosen)
    const e = bag(expected)
    const sameBag = c.length === e.length && c.every((w, i) => w === e[i])

    if (sameBag) {
        const sameOrder = chosen.map(lookupKey).join(' ') === expected.map(lookupKey).join(' ')

        if (sameOrder) return { correct: true, issues: [] }

        if (orderFree) {
            return {
                correct: true,
                issues: [],
                note: {
                    hi: `सही! संस्कृत में शब्द-क्रम लचीला है क्योंकि विभक्ति-प्रत्यय अर्थ बताते हैं। सबसे सामान्य क्रम: ${expected.join(' ')}।`,
                    en: `Correct! Sanskrit word order is flexible because the endings carry the meaning. The most common order is: ${expected.join(' ')}.`
                }
            }
        }

        return {
            correct: false,
            issues: [{
                code: 'order',
                text: {
                    hi: `शब्द सही हैं, पर यहाँ क्रम महत्त्वपूर्ण है: ${expected.join(' ')}।`,
                    en: `The words are right, but here the order matters: ${expected.join(' ')}.`
                }
            }]
        }
    }

    const issues: SentenceIssue[] = []
    const remainingExpected = [...expected]
    const unmatchedChosen: string[] = []

    for (const w of chosen) {
        const i = remainingExpected.findIndex(x => lookupKey(x) === lookupKey(w))
        if (i >= 0) remainingExpected.splice(i, 1)
        else unmatchedChosen.push(w)
    }

    const stillMissing: string[] = []

    for (const exp of remainingExpected) {
        const j = unmatchedChosen.findIndex(w => describeDiff(w, exp) !== null)

        if (j >= 0) {
            const diff = describeDiff(unmatchedChosen[j], exp)
            if (diff) issues.push(diff)
            unmatchedChosen.splice(j, 1)
        }
        else {
            stillMissing.push(exp)
        }
    }

    for (const w of unmatchedChosen) {
        issues.push({
            code: 'extra',
            text: { hi: `${w} की यहाँ आवश्यकता नहीं है।`, en: `${w} is not needed here.` }
        })
    }

    if (stillMissing.length > 0) {
        issues.push({
            code: 'missing',
            text: {
                hi: `वाक्य में एक शब्द कम है (${stillMissing.length})। अर्थ फिर से पढ़कर देखें कि क्या छूट गया।`,
                en: `Your sentence is missing ${stillMissing.length === 1 ? 'a word' : `${stillMissing.length} words`}. Re-read the meaning and see what is left out.`
            }
        })
    }

    const agreement = checkAgreement(chosen)

    for (const a of agreement) {
        if (!issues.some(i => i.code === a.code)) issues.push(a)
    }

    return { correct: false, issues }
}

/** Analyse every word of a sentence (used by the sentence analyser and the story reader). */
export const analyzeSentence = (sentence: string): Array<{ text: string; isWord: boolean; analyses: Analysis[] }> => {
    return tokenizeSentence(sentence).map(token => ({
        ...token,
        analyses: token.isWord ? analyzeWord(token.text) : []
    }))
}

/**
 * Feature-level difference between a wrong choice and the right answer,
 * e.g. ['vacana:eka>bahu'] when पठामः was chosen for पठामि. Feeds the weakness tracker.
 */
export const confusionsBetween = (chosen: string, correct: string): string[] => {
    const cv = verbsOf(chosen)
    const ev = verbsOf(correct)
    const out: string[] = []

    if (cv.length > 0 && ev.length > 0) {
        const e = ev[0]
        const c = cv.find(x => x.dhatuId === e.dhatuId) ?? cv[0]
        if (c.lakara !== e.lakara) out.push(`lakara:${e.lakara}>${c.lakara}`)
        if (c.purusha !== e.purusha) out.push(`purusha:${e.purusha}>${c.purusha}`)
        if (c.vacana !== e.vacana) out.push(`vacana:${e.vacana}>${c.vacana}`)
        return out
    }

    const cn = nominalOf(chosen)
    const en = nominalOf(correct)

    if (cn.length > 0 && en.length > 0) {
        const e = en[0]
        const c = cn.find(x => x.stem === e.stem) ?? cn[0]
        if (c.vibhakti !== e.vibhakti) out.push(`vibhakti:${e.vibhakti}>${c.vibhakti}`)
        if (c.vacana !== e.vacana) out.push(`vacana:${e.vacana}>${c.vacana}`)
        if (c.gender !== e.gender) out.push(`gender:${e.gender}>${c.gender}`)
    }

    return out
}
